import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, Clock, Heart, Activity, ChevronRight, AlertCircle, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../store/authStore';
import { getSessions } from '../../api/sessions';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { Button } from '../../components/Button';
type DateRange = 'today' | '7days' | '30days' | '3months' | 'custom';
export function SessionsList() {
  const navigate = useNavigate();
  const {
    token
  } = useAuth();
  const [sessions, setSessions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeRange, setActiveRange] = useState<DateRange>('30days');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [showCustomSheet, setShowCustomSheet] = useState(false);
  const fetchSessions = async (range: DateRange, fromDate?: string, toDate?: string) => {
    if (!token) return;
    setIsLoading(true);
    setError(false);
    try {
      const now = new Date();
      let from = new Date();
      let to = new Date();
      if (range === 'today') {
        from.setHours(0, 0, 0, 0);
      } else if (range === '7days') {
        from.setDate(now.getDate() - 7);
        from.setHours(0, 0, 0, 0);
      } else if (range === '30days') {
        from.setDate(now.getDate() - 30);
        from.setHours(0, 0, 0, 0);
      } else if (range === '3months') {
        from.setDate(now.getDate() - 90);
        from.setHours(0, 0, 0, 0);
      } else if (range === 'custom' && fromDate && toDate) {
        from = new Date(fromDate);
        to = new Date(toDate);
        to.setHours(23, 59, 59, 999);
      }
      const res = await getSessions(token, {
        from: from.toISOString(),
        to: to.toISOString()
      });
      setSessions(res || []);
    } catch (err) {
      // console.error('Failed to fetch sessions', err)
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (activeRange !== 'custom') {
      fetchSessions(activeRange);
    }
  }, [activeRange, token]);
  const handleCustomApply = () => {
    if (customFrom && customTo) {
      setActiveRange('custom');
      fetchSessions('custom', customFrom, customTo);
      setShowCustomSheet(false);
    }
  };
  // Compute Stats
  const stats = useMemo(() => {
    let totalDuration = 0;
    let bpmSum = 0;
    let bpmCount = 0;
    sessions.forEach(s => {
      if (s.duration) totalDuration += s.duration;
      if (typeof s.avgBpm === 'number' && s.avgBpm > 0) {
        bpmSum += s.avgBpm;
        bpmCount += 1;
      }
    });
    const h = Math.floor(totalDuration / 60);
    const m = totalDuration % 60;
    return {
      total: sessions.length,
      time: `${h}h ${m}m`,
      avgBpm: bpmCount > 0 ? Math.round(bpmSum / bpmCount) : null
    };
  }, [sessions]);
  // Group Sessions by Date
  const groupedSessions = useMemo(() => {
    const groups: Record<string, any[]> = {};
    sessions.forEach(session => {
      const date = new Date(session.createdAt || Date.now());
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      let dateKey = date.toLocaleDateString('en-US', {
        weekday: 'long',
        day: 'numeric',
        month: 'short'
      });
      if (date.toDateString() === today.toDateString()) dateKey = 'Today';else if (date.toDateString() === yesterday.toDateString()) dateKey = 'Yesterday';
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(session);
    });
    return groups;
  }, [sessions]);
  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };
  const formatDuration = (minutes: number) => {
    if (!minutes) return '0m';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };
  const getStatusPill = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="bg-danger/15 text-danger px-2 py-0.5 rounded-pill text-[12px] font-medium" data-id="element-1100">
            Active
          </span>;
      case 'incomplete':
        return <span className="bg-amber-500/15 text-amber-600 px-2 py-0.5 rounded-pill text-[12px] font-medium" data-id="element-1101">
            Incomplete
          </span>;
      default:
        return <span className="bg-accent/20 text-accent px-2 py-0.5 rounded-pill text-[12px] font-medium" data-id="element-1102">
            Completed
          </span>;
    }
  };
  return <main className="w-full h-full flex flex-col pb-8" data-id="element-1103">
      {error && <div className="bg-danger/10 border border-danger/20 rounded-[12px] p-3 mb-6 flex items-center justify-between" data-id="element-1104">
          <div className="flex items-center text-danger" data-id="element-1105">
            <AlertCircle size={18} className="mr-2" data-id="element-1106" />
            <span className="text-[14px] font-medium" data-id="element-1107">
              Failed to load sessions.
            </span>
          </div>
          <button onClick={() => fetchSessions(activeRange, customFrom, customTo)} className="text-danger text-[14px] font-semibold hover:underline" data-id="element-1108">
            Retry
          </button>
        </div>}

      {/* Header */}
      <div className="mb-6" data-id="element-1109">
        <h1 className="text-[24px] font-semibold text-text-primary" data-id="element-1110">
          Session History
        </h1>
        <p className="text-[14px] text-text-secondary mt-1" data-id="element-1111">
          {sessions.length} sessions recorded
        </p>
      </div>

      {/* Filter Bar */}
      <div className="flex overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 hide-scrollbar space-x-2 mb-6" data-id="element-1112">
        {[{
        id: 'today',
        label: 'Today'
      }, {
        id: '7days',
        label: 'Last 7 days'
      }, {
        id: '30days',
        label: 'Last 30 days'
      }, {
        id: '3months',
        label: 'Last 3 months'
      }, {
        id: 'custom',
        label: 'Custom range'
      }].map(filter => <button key={filter.id} onClick={() => filter.id === 'custom' ? setShowCustomSheet(true) : setActiveRange(filter.id as DateRange)} className={`flex-shrink-0 h-[36px] px-4 rounded-pill text-[14px] font-medium transition-colors duration-200 ${activeRange === filter.id ? 'bg-primary text-white' : 'bg-border text-text-secondary hover:bg-border/80'}`} data-id="element-1113">
            {filter.label}
          </button>)}
      </div>

      {/* Summary Stats */}
      <div className="flex overflow-x-auto pb-4 -mx-4 px-4 md:mx-0 md:px-0 hide-scrollbar space-x-3 mb-6" data-id="element-1114">
        <div className="bg-white rounded-[16px] shadow-sm p-3 min-w-[140px] flex-shrink-0" data-id="element-1115">
          <ClipboardList size={20} className="text-primary mb-2" data-id="element-1116" />
          {isLoading ? <SkeletonBlock height="24px" width="50%" className="mb-1" data-id="element-1117" /> : <div className="text-[22px] font-bold text-text-primary leading-none mb-1" data-id="element-1118">
              {stats.total}
            </div>}
          <div className="text-[12px] text-text-secondary" data-id="element-1119">Total Sessions</div>
        </div>
        <div className="bg-white rounded-[16px] shadow-sm p-3 min-w-[140px] flex-shrink-0" data-id="element-1120">
          <Clock size={20} className="text-accent mb-2" data-id="element-1121" />
          {isLoading ? <SkeletonBlock height="24px" width="70%" className="mb-1" data-id="element-1122" /> : <div className="text-[22px] font-bold text-text-primary leading-none mb-1" data-id="element-1123">
              {stats.time}
            </div>}
          <div className="text-[12px] text-text-secondary" data-id="element-1124">Total Time</div>
        </div>
        <div className="bg-white rounded-[16px] shadow-sm p-3 min-w-[140px] flex-shrink-0" data-id="element-1125">
          <Heart size={20} className="text-primary mb-2" data-id="element-1126" />
          {isLoading ? <SkeletonBlock height="24px" width="50%" className="mb-1" data-id="element-1127" /> : <div className="text-[22px] font-bold text-text-primary leading-none mb-1" data-id="element-1128">
              {stats.avgBpm !== null ? stats.avgBpm : '--'}
            </div>}
          <div className="text-[12px] text-text-secondary" data-id="element-1129">Avg BPM</div>
        </div>
      </div>

      {/* Sessions List */}
      <div className="flex-1" data-id="element-1130">
        {isLoading ? <div className="space-y-4" data-id="element-1131">
            {[1, 2, 3, 4, 5].map(i => <SkeletonBlock key={i} height="120px" borderRadius="20px" data-id="element-1132" />)}
          </div> : sessions.length === 0 ? <div className="flex flex-col items-center justify-center py-12 text-center" data-id="element-1133">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-4" data-id="element-1134">
              <Calendar size={32} className="text-primary" data-id="element-1135" />
            </div>
            <h2 className="text-[20px] font-semibold text-text-primary mb-2" data-id="element-1136">
              No sessions found
            </h2>
            <p className="text-[14px] text-text-secondary mb-6 max-w-[250px]" data-id="element-1137">
              No monitoring sessions recorded for this period.
            </p>
            {activeRange === 'today' && <Button onClick={() => navigate('/app/monitor')} className="max-w-[200px]" data-id="element-1138">
                Start Monitoring
              </Button>}
          </div> : <div className="space-y-8" data-id="element-1139">
            {Object.entries(groupedSessions).map(([dateLabel, groupSessions]) => <div key={dateLabel} data-id="element-1140">
                  <div className="flex items-center mb-4" data-id="element-1141">
                    <span className="text-[13px] font-semibold text-text-secondary uppercase tracking-[1px] mr-4" data-id="element-1142">
                      {dateLabel}
                    </span>
                    <div className="flex-1 h-[1px] bg-border" data-id="element-1143" />
                  </div>
                  <div className="space-y-3" data-id="element-1144">
                    {groupSessions.map((session, i) => <div key={session.id || i} onClick={() => navigate(`/app/history/${session.id}`)} className="bg-white rounded-[20px] shadow-sm p-4 cursor-pointer hover:-translate-y-[2px] hover:shadow-card transition-all" data-id="element-1145">
                        <div className="flex items-center justify-between mb-4" data-id="element-1146">
                          <div className="flex items-center" data-id="element-1147">
                            <div className="w-[44px] h-[44px] bg-primary rounded-full flex items-center justify-center mr-4 flex-shrink-0" data-id="element-1148">
                              <Activity size={20} className="text-white" data-id="element-1149" />
                            </div>
                            <div data-id="element-1150">
                              <h3 className="text-[15px] font-semibold text-text-primary" data-id="element-1151">
                                {new Date(session.createdAt || Date.now()).toLocaleDateString('en-US', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short'
                      })}{' '}
                                · {formatTime(session.createdAt || Date.now())}
                              </h3>
                              <p className="text-[13px] text-text-secondary mt-0.5" data-id="element-1152">
                                {formatDuration(session.duration)}
                              </p>
                            </div>
                          </div>
                          <ChevronRight size={20} className="text-text-secondary" data-id="element-1153" />
                        </div>

                        <div className="h-[1px] w-full bg-border mb-3" data-id="element-1154" />

                        <div className="flex items-center justify-between px-2" data-id="element-1155">
                          <div className="flex flex-col items-center" data-id="element-1156">
                            <span className="text-[11px] text-text-secondary uppercase tracking-wider mb-1" data-id="element-1157">
                              Avg BPM
                            </span>
                            <span className="text-[13px] font-semibold text-text-primary" data-id="element-1158">
                              {typeof session.avgBpm === 'number' && session.avgBpm > 0 ? Math.round(session.avgBpm) : '--'}
                            </span>
                          </div>
                          <div className="w-[1px] h-[24px] bg-border" data-id="element-1159" />
                          <div className="flex flex-col items-center" data-id="element-1160">
                            <span className="text-[11px] text-text-secondary uppercase tracking-wider mb-1" data-id="element-1161">
                              Duration
                            </span>
                            <span className="text-[13px] font-semibold text-text-primary" data-id="element-1162">
                              {formatDuration(session.duration)}
                            </span>
                          </div>
                          <div className="w-[1px] h-[24px] bg-border" data-id="element-1163" />
                          <div className="flex flex-col items-center" data-id="element-1164">
                            <span className="text-[11px] text-text-secondary uppercase tracking-wider mb-1" data-id="element-1165">
                              Status
                            </span>
                            {getStatusPill(session.sessionStatus || 'completed')}
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>)}
          </div>}
      </div>

      {/* Custom Date Range Bottom Sheet */}
      <AnimatePresence data-id="element-1166">
        {showCustomSheet && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowCustomSheet(false)} className="fixed inset-0 bg-black/40 z-[110]" data-id="element-1167" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-[24px] p-6 pb-[calc(24px+env(safe-area-inset-bottom))] z-[120] md:max-w-[400px] md:left-1/2 md:-translate-x-1/2 md:bottom-6 md:rounded-[24px]" data-id="element-1168">
              <div className="w-[40px] h-[4px] bg-border rounded-pill mx-auto mb-6" data-id="element-1169" />
              <h2 className="text-[20px] font-semibold text-text-primary mb-6" data-id="element-1170">
                Custom Date Range
              </h2>

              <div className="flex space-x-4 mb-8" data-id="element-1171">
                <div className="flex-1" data-id="element-1172">
                  <label htmlFor="customFrom" className="block text-[13px] text-text-secondary mb-2" data-id="element-1173">
                    From
                  </label>
                  <input id="customFrom" type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="w-full h-[52px] rounded-input border border-border px-4 text-text-primary focus:border-primary focus:shadow-focus outline-none transition-all" data-id="element-1174" />
                </div>
                <div className="flex-1" data-id="element-1175">
                  <label htmlFor="customTo" className="block text-[13px] text-text-secondary mb-2" data-id="element-1176">
                    To
                  </label>
                  <input id="customTo" type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="w-full h-[52px] rounded-input border border-border px-4 text-text-primary focus:border-primary focus:shadow-focus outline-none transition-all" data-id="element-1177" />
                </div>
              </div>

              <div className="flex flex-col space-y-3" data-id="element-1178">
                <Button onClick={handleCustomApply} disabled={!customFrom || !customTo} data-id="element-1179">
                  Apply
                </Button>
                <button onClick={() => setShowCustomSheet(false)} className="w-full h-[52px] text-text-secondary font-medium rounded-btn hover:bg-border/50 transition-colors" data-id="element-1180">
                  Cancel
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      <style data-id="element-1181">{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </main>;
}